import type {
  MultipleOpportunity,
  SingleOpportunity,
  WalletTypeEnum
} from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { floor } from 'lodash';
import { useEffect } from 'react';
import { useField, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';

import { getWalletFieldName as getPromotionName } from '../utils/getWalletFieldName';

import { useFormMutators } from './useFormMutators';

const usePromotionCheckbox = (
  walletType: WalletTypeEnum,
  opportunity: SingleOpportunity | MultipleOpportunity,
  fieldName: string,
  numberOfLines: number
) => {
  const { values } = useFormState<FormData>({ subscription: { values: true } });
  const mutators = useFormMutators();
  const wallets = useSelector(select.betSlip.wallets);
  const isBonusCashEnabled = useFeature(FeatureFlag.ISPBonusCash);
  const isFreeBetsEnabled = useFeature(FeatureFlag.ISPSportsFreeBets);

  const promotionId = getIsAvailableOpportunity(opportunity)
    ? opportunity.applicableWallets?.find((walletId) => wallets[walletId]?.type === walletType)
    : undefined;

  const wallet = promotionId ? wallets[promotionId] : undefined;
  const promotionRedeemable = wallet?.redeemable;

  const promotionName = getPromotionName(walletType, promotionRedeemable);

  const { input: promotionInput } = useField(`${fieldName}.${promotionName}`, {
    type: 'checkbox',
    value: promotionId,
    subscription: { value: true }
  });

  const isBonusCash = promotionRedeemable === true;
  const isFreeBet = promotionRedeemable === false;
  const isFeatureEnabled = (isBonusCash && isBonusCashEnabled) || (isFreeBet && isFreeBetsEnabled);

  const isEligibleForPromotion =
    Boolean(promotionId) && getIsAvailableOpportunity(opportunity) && opportunity.hasBonusMoney;

  const [promotionFieldName] =
    Object.entries(values).find(
      ([_key, value]) =>
        Array.isArray(value[promotionName]) && value[promotionName][0] === promotionId
    ) ?? [];

  const promotionStake = wallet ? String(floor(wallet.amount / numberOfLines, 2)) : undefined;

  // When the checkbox is checked, set the stake per line field
  useEffect(() => {
    if (promotionInput.checked && promotionStake) {
      mutators.setFieldValue(`${fieldName}.stakePerLine`, promotionStake);
    }
  }, [promotionInput.checked, promotionStake, fieldName, mutators]);

  // When the checkbox is unchecked, clear the extra wallet field
  useEffect(() => {
    if (!promotionInput.checked) {
      mutators.clearFieldValue(`${fieldName}.stakePerLine`);
      mutators.clearFieldValue(`${fieldName}.bonusCash`);
      mutators.clearFieldValue(`${fieldName}.freeBets`);
    }
  }, [promotionInput.checked, fieldName, mutators]);

  // If the opportunity is no longer available, clear the extra wallet field
  useEffect(() => {
    if (!getIsAvailableOpportunity(opportunity)) {
      mutators.clearFieldValue(promotionInput.name);
    }
  }, [promotionInput.name, mutators, opportunity]);

  // Clear the extra wallet field if the opportunity is no longer eligible for the extra wallet
  useEffect(() => {
    if (!isEligibleForPromotion) {
      mutators.clearFieldValue(promotionInput.name);
    }
  }, [promotionInput.name, isEligibleForPromotion, mutators]);

  return {
    isEligibleForPromotion,
    isFeatureEnabled,
    promotionFieldName,
    promotionInput,
    promotionId,
    promotionRedeemable
  };
};

export default usePromotionCheckbox;
